/**
 * passkeyApi.js — Thin wrappers around the WebAuthn ceremony.
 *
 * The browser's `navigator.credentials.create / .get` API speaks raw
 * ArrayBuffers; @simplewebauthn/browser handles the JSON ↔ ArrayBuffer
 * translation. We just orchestrate the two HTTP calls per ceremony.
 */
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { authFetch } from './authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Enrol a new passkey for the currently-logged-in user. */
export async function enrollPasskey(nickname = 'Passkey') {
  const beginRes = await authFetch(`${BACKEND_URL}/api/passkey/register/begin`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!beginRes.ok) {
    const err = await beginRes.json().catch(() => ({}));
    throw new Error(err.detail || `register/begin failed (${beginRes.status})`);
  }
  const options = await beginRes.json();
  // Browser pops up the platform authenticator (Face ID / Touch ID / etc.).
  const credential = await startRegistration({ optionsJSON: options });
  const completeRes = await authFetch(`${BACKEND_URL}/api/passkey/register/complete`, {
    method: 'POST',
    body: JSON.stringify({ credential, nickname }),
  });
  if (!completeRes.ok) {
    const err = await completeRes.json().catch(() => ({}));
    throw new Error(err.detail || `register/complete failed (${completeRes.status})`);
  }
  return completeRes.json();
}

/** Sign in with a passkey. Returns the user record on success. */
export async function loginWithPasskey(email) {
  const beginRes = await fetch(`${BACKEND_URL}/api/passkey/login/begin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });
  if (!beginRes.ok) {
    const err = await beginRes.json().catch(() => ({}));
    throw new Error(err.detail || `login/begin failed (${beginRes.status})`);
  }
  const options = await beginRes.json();
  const credential = await startAuthentication({ optionsJSON: options });
  const completeRes = await fetch(`${BACKEND_URL}/api/passkey/login/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, credential }),
  });
  if (!completeRes.ok) {
    const err = await completeRes.json().catch(() => ({}));
    throw new Error(err.detail || `login/complete failed (${completeRes.status})`);
  }
  return completeRes.json();
}

export async function listPasskeys() {
  const res = await authFetch(`${BACKEND_URL}/api/passkey/list`);
  if (!res.ok) throw new Error('Failed to load passkeys');
  return res.json();
}

export async function deletePasskey(credentialId) {
  const res = await authFetch(
    `${BACKEND_URL}/api/passkey/${encodeURIComponent(credentialId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to delete passkey');
  }
  return res.json();
}

/** True iff the browser supports the WebAuthn API at all. */
export const isPasskeySupported = () =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential;
